import React, { useState, useEffect, useCallback } from 'react';
import { TextField } from '@mui/material';

// Debounce-Funktion
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

interface LocalNotesFieldProps {
  label: string;
  initialValue: string;
  onLocalChange: (value: string) => void;
  disabled: boolean;
  placeholder?: string;
  inputProps?: any;
  sx?: any;
}

// Lokales Notizfeld für bessere Performance
const LocalNotesField = React.memo<LocalNotesFieldProps>(({
  label,
  initialValue,
  onLocalChange,
  disabled,
  placeholder,
  inputProps = {},
  sx = {}
}) => {
  const [localValue, setLocalValue] = useState(initialValue);

  // Update lokaler Wert wenn sich initialValue ändert
  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  // Debounced Change für Parent-Update
  const debouncedChange = useCallback(
    debounce((value: string) => {
      onLocalChange(value);
    }, 300),
    [onLocalChange]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue); // Sofortiges lokales Update
    debouncedChange(newValue); // Verzögertes Parent-Update
  }, [debouncedChange]);

  return (
    <TextField
      fullWidth
      multiline
      minRows={1}
      label={label}
      value={localValue}
      disabled={disabled}
      onChange={handleChange}
      placeholder={placeholder}
      size="small"
      sx={{ 
        mb: 2, 
        ...sx,
        '& .MuiInputBase-input': { overflow: 'hidden', resize: 'vertical' },
        '& .MuiInputBase-inputMultiline': { overflow: 'hidden', resize: 'vertical' }
      }}
      inputProps={{ style: { fontSize: '0.875rem' }, ...inputProps }}
    />
  );
});

LocalNotesField.displayName = 'LocalNotesField';

export default LocalNotesField;